import {
  useAddFullProductKitToCartMutation,
  useAddProductKitToCartMutation,
} from '@graphql/mutations/__generated__/product-reflection.generated'
import { ProductCardVariantQuery } from '@graphql/queries/__generated__/product-card.generated'
import { useVariantKitDataLazyQuery } from '@graphql/queries/__generated__/product-variant-kit.generated'
import BoxIcon from '@icons/BoxIcon'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_PRODUCT_QTY } from '../../../../constants'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import { mutationWarningsToMessages } from '../../../misc/utils'
import KitQuantityStepper from '../VariantKitPanel/KitQuantityStepper'
import VariantKitPanel, { ISupplyFormData } from '../VariantKitPanel/VariantKitPanel'
import {
  AddedToCartResult,
  ExtraRow,
  LinkButton,
  fullKitPriceRange,
  multiplyPriceRange,
  Price,
  PriceRange,
  pushAddToCartEvent,
  SubmitButton,
  updateCacheAfterAdd,
} from './common'

type Variant = ProductCardVariantQuery['productVariant']

// Unit price of the kit the main button adds: the saved default kit, otherwise the full kit.
const getKitUnitPriceRange = (variant: Variant): PriceRange => {
  if (variant.isCustomizable && variant.hasDefaultKit && variant.defaultKitPrice) {
    const { currency, gross } = variant.defaultKitPrice
    return { currency, discount: { min: gross, max: gross }, undiscount: { min: gross, max: gross } }
  }
  return fullKitPriceRange(variant.priceRange)
}

interface Props {
  variant: Variant
  productName: string
  isArchive: boolean
  categoryName?: string

  onAdded(result: AddedToCartResult): void
}

// Kit form of the product card panel. `quantity` is the number of kits, passed to every add-to-cart path
// (full kit, default kit and "Customize the KIT"), as on the product page.
const KitSelection: React.FC<Props> = ({ variant, productName, isArchive, categoryName, onAdded }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [quantity, setQuantity] = useState(1)
  const [adding, setAdding] = useState(false)
  const [kitFormData, setKitFormData] = useState<ISupplyFormData | null>(null)
  const [isKitPanelOpen, setIsKitPanelOpen] = useState(false)
  const [kitSubmitting, setKitSubmitting] = useState(false)

  const [loadKitData, kitDataQuery] = useVariantKitDataLazyQuery({ fetchPolicy: 'network-only' })
  const [addFullKitToCart] = useAddFullProductKitToCartMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => updateCacheAfterAdd(cache, data?.addFullKitToCart),
  })
  const [addKitToCart] = useAddProductKitToCartMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => {
      if (!data?.addKitToCart.errors.length) updateCacheAfterAdd(cache, data?.addKitToCart)
    },
  })

  const unitPriceRange = getKitUnitPriceRange(variant)
  const canCustomize = variant.isCustomizable
  // A customizable kit without a saved default can only be customized.
  const canAddWholeKit = !variant.isCustomizable || variant.hasDefaultKit
  const isAvailable = variant.stockStatus !== OutOfStockStatusEnum.Out && !isArchive

  const onSuccess = (result: AddedToCartResult & { warnings?: { messages?: string[] }[] }) => {
    mutationWarningsToMessages(result.warnings).forEach(message => enqueueSnackbar(message, { variant: 'warning' }))
    pushAddToCartEvent(result, {
      name: productName,
      categoryName,
      currency: unitPriceRange.currency,
      unitPrice: unitPriceRange.discount.max,
    })
    // The panel covers this form with AddedToCartNotice and then closes, so no state updates after this point.
    onAdded(result)
  }

  const onAddWholeKit = async () => {
    setAdding(true)
    try {
      const { data } = await addFullKitToCart({ variables: { pk: variant.pk, quantity } })
      onSuccess(data.addFullKitToCart)
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
      setAdding(false)
    }
  }

  const onCustomize = async () => {
    const { data, error } = await loadKitData({ variables: { pk: variant.pk } })
    if (error || !data?.productVariant.customizeKitData) {
      enqueueSnackbar(error?.message || t('productCard.loadError', "Couldn't load product options."), {
        variant: 'error',
      })
      return
    }
    setKitFormData(data.productVariant.customizeKitData)
    setIsKitPanelOpen(true)
  }

  const onSubmitKit = async (
    supplyLine: number | null,
    variants: { pk: number; quantity: number }[],
    setAsDefault: boolean
  ) => {
    setKitSubmitting(true)
    try {
      const { data } = await addKitToCart({
        variables: {
          pk: variant.pk,
          variants: variants.map(({ pk, quantity: partQuantity }) => ({ pk, quantity: partQuantity })),
          setAsDefault,
          quantity,
        },
      })
      const errors = data?.addKitToCart.errors || []
      if (errors.length) {
        throw Error(errors[0].messages[0])
      }
      setIsKitPanelOpen(false)
      onSuccess(data.addKitToCart)
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
      setKitSubmitting(false)
    }
  }

  const customizeLoading = kitDataQuery.loading
  const busy = adding || customizeLoading
  const mainLoading = canAddWholeKit ? adding : customizeLoading

  return (
    <>
      <div className='product-card-panel__price-row product-card-panel__price-row--stepper'>
        <KitQuantityStepper quantity={quantity} availableQuantity={MAX_PRODUCT_QTY} onChangeQuantity={setQuantity} />
        <Price range={multiplyPriceRange(unitPriceRange, quantity)} />
      </div>
      <ExtraRow>
        {canCustomize && canAddWholeKit && (
          <LinkButton loading={customizeLoading} disabled={!isAvailable || busy} onClick={onCustomize}>
            {t('button.customizeTheKit', 'Customize the KIT')}
          </LinkButton>
        )}
      </ExtraRow>
      <SubmitButton
        loading={mainLoading}
        icon={<BoxIcon viewBox='0 -960 960 960' />}
        disabled={!isAvailable || busy}
        onClick={canAddWholeKit ? onAddWholeKit : onCustomize}
      >
        {!canAddWholeKit
          ? t('button.customizeTheKit', 'Customize the KIT')
          : variant.isCustomizable
          ? t('button.addDefaultKitToCart', 'Add Default KIT to Cart')
          : t('productCard.addKitToCart', 'Add KIT to Cart')}
      </SubmitButton>
      <VariantKitPanel
        isOpen={isKitPanelOpen}
        onOpen={() => setIsKitPanelOpen(true)}
        onClosePanel={() => setIsKitPanelOpen(false)}
        onSubmit={onSubmitKit}
        loading={kitSubmitting}
        data={kitFormData}
      />
    </>
  )
}

export default KitSelection
